import { INormalizedTheme } from './normalized-theme.interface';
import { ITheme } from './theme.interface';
import { IThemeModes } from './theme-modes.interface';
import { ThemeScale } from './theme-scale.type';
import { ThemeValueKind } from './theme-value-kind.type';

/**
 * Turns the contents of a theme file into a complete theme (IC-840). Whatever
 * the file leaves out comes from the base (the default theme), so a theme may
 * set only the values it changes; whatever is invalid is dropped with a
 * warning, so a bad file never breaks the app.
 */
export class ThemeNormalizer {
  private static readonly _length = /^(0|-?\d*\.?\d+(px|rem|em|%|vh|vw))$/;
  private static readonly _number = /^\d*\.?\d+$/;
  private static readonly _color = /^(#[0-9a-f]{3,8}|(rgb|rgba|hsl|hsla)\([^()]*\)|transparent|currentcolor|[a-z]+)$/i;
  private static readonly _key = /^[a-z0-9][a-z0-9.-]*$/i;
  // Characters that could close the generated rule or start a new one.
  private static readonly _unsafe = /[;{}<>]/;

  static normalize(input: unknown, base: ITheme): INormalizedTheme {
    const warnings: string[] = [];
    const source = ThemeNormalizer._isRecord(input) ? input : {};
    if (!ThemeNormalizer._isRecord(input)) warnings.push('tema nu este un obiect JSON; folosesc tema implicită');

    const rawId = source['id'];
    const id = typeof rawId === 'string' && ThemeNormalizer._key.test(rawId) ? rawId : base.id;
    if (rawId !== undefined && id !== rawId) warnings.push('id: invalid, folosesc id-ul temei implicite');
    const rawName = source['name'];
    const name = typeof rawName === 'string' && rawName.trim() ? rawName.trim() : id;
    const font = ThemeNormalizer._isRecord(source['font']) ? source['font'] : {};

    const theme: ITheme = {
      id,
      name,
      font: {
        family: ThemeNormalizer._scale(base.font.family, font['family'], 'font', 'font.family', warnings),
        size: ThemeNormalizer._scale(base.font.size, font['size'], 'length', 'font.size', warnings),
        weight: ThemeNormalizer._scale(base.font.weight, font['weight'], 'number', 'font.weight', warnings),
        leading: ThemeNormalizer._scale(base.font.leading, font['leading'], 'number', 'font.leading', warnings),
      },
      space: ThemeNormalizer._scale(base.space, source['space'], 'length', 'space', warnings),
      radius: ThemeNormalizer._scale(base.radius, source['radius'], 'length', 'radius', warnings),
      border: ThemeNormalizer._scale(base.border, source['border'], 'length', 'border', warnings),
      control: ThemeNormalizer._scale(base.control, source['control'], 'length', 'control', warnings),
      colors: ThemeNormalizer._modes(base.colors, source['colors'], 'color', 'colors', warnings),
      shadow: ThemeNormalizer._modes(base.shadow, source['shadow'], 'shadow', 'shadow', warnings),
      sections: ThemeNormalizer._sections(base.sections, source['sections'], warnings),
    };

    return { theme, warnings };
  }

  private static _isValid(value: string, kind: ThemeValueKind): boolean {
    if (ThemeNormalizer._unsafe.test(value)) return false;
    const v = value.trim();
    if (!v) return false;
    if (kind === 'length' && /^calc\(.*\)$/i.test(v)) return true;
    switch (kind) {
      case 'color':
        return ThemeNormalizer._color.test(v);
      case 'length':
        return ThemeNormalizer._length.test(v);
      case 'number':
        return ThemeNormalizer._number.test(v);
      case 'font':
      case 'shadow':
        return true;
    }
  }

  private static _isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  /** The base scale with the file's valid values on top. New keys are allowed (a theme may add a colour). */
  private static _scale(base: ThemeScale, input: unknown, kind: ThemeValueKind, path: string, warnings: string[]): ThemeScale {
    const result: ThemeScale = { ...base };
    if (input === undefined) return result;
    if (!ThemeNormalizer._isRecord(input)) {
      warnings.push(`${path}: trebuie să fie un obiect; păstrez valorile implicite`);
      return result;
    }
    for (const [key, value] of Object.entries(input)) {
      if (!ThemeNormalizer._key.test(key)) {
        warnings.push(`${path}.${key}: nume invalid, ignorat`);
        continue;
      }
      if (typeof value !== 'string' || !ThemeNormalizer._isValid(value, kind)) {
        warnings.push(`${path}.${key}: valoare invalidă (${JSON.stringify(value)}), ignorată`);
        continue;
      }
      result[key] = value.trim();
    }
    return result;
  }

  private static _modes(base: IThemeModes, input: unknown, kind: ThemeValueKind, path: string, warnings: string[]): IThemeModes {
    const source = ThemeNormalizer._isRecord(input) ? input : {};
    if (input !== undefined && !ThemeNormalizer._isRecord(input)) warnings.push(`${path}: trebuie să fie un obiect cu light și dark`);
    return {
      light: ThemeNormalizer._scale(base.light, source['light'], kind, `${path}.light`, warnings),
      dark: ThemeNormalizer._scale(base.dark, source['dark'], kind, `${path}.dark`, warnings),
    };
  }

  private static _sections(base: ITheme['sections'], input: unknown, warnings: string[]): ITheme['sections'] {
    const result: ITheme['sections'] = { ...base };
    if (input === undefined) return result;
    if (!ThemeNormalizer._isRecord(input)) {
      warnings.push('sections: trebuie să fie un obiect');
      return result;
    }
    for (const [name, value] of Object.entries(input)) {
      if (!ThemeNormalizer._key.test(name)) {
        warnings.push(`sections.${name}: nume invalid, ignorat`);
        continue;
      }
      const baseSection = result[name] ?? {};
      result[name] = ThemeNormalizer._modes(
        { light: baseSection.light ?? {}, dark: baseSection.dark ?? {} },
        value,
        'color',
        `sections.${name}`,
        warnings,
      );
    }
    return result;
  }
}
